import React, { useEffect, useState } from "react";
import { Send, Search, Loader2, Trash2, UserRound, X } from "lucide-react";
import { toast } from "sonner";
import { fetchLocationMyServices, searchLocationCustomers, sendLocationToCustomer, fetchSentLocationShares, revokeLocationShare } from "@/lib/api";
import { captureLocation, apiFail, live, fmt, primary, danger, btn, GONE } from "@/components/maps/LocationShare";

function CustomerPicker({ picked, setPicked }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (picked || q.trim().length < 2) { setRes([]); return undefined; }
    let on = true;
    setBusy(true);
    const t = setTimeout(() => searchLocationCustomers(q.trim()).then((r) => { if (on) { setRes(r); setFailed(false); } }).catch((e) => { if (!on) return; setRes([]); setFailed(true); if (e?.response?.status === 429) toast.error(e.response.data?.detail || "Thodi der baad try karein"); }).finally(() => on && setBusy(false)), 350);
    return () => { on = false; clearTimeout(t); };
  }, [q, picked]);
  if (picked) return (
    <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm min-w-0" data-testid="send-loc-picked">
      <UserRound className="w-4 h-4 text-emerald-700 flex-shrink-0" /><span className="min-w-0"><span className="block font-semibold truncate">{picked.name}</span><span className="block text-xs text-slate-500 truncate">{picked.hint}</span></span>
      <button type="button" onClick={() => setPicked(null)} className="ms-auto p-1 text-slate-500" aria-label="Change customer" data-testid="send-loc-clear"><X className="w-4 h-4" /></button>
    </div>
  );
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
        <Search className="w-4 h-4 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Naam ke 2-3 akshar, ya poora mobile/email" className="w-full bg-transparent outline-none text-sm" data-testid="send-loc-search" />
        {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
      </div>
      {q.trim().length >= 2 && !busy && !failed && !res.length && <p className="text-xs text-slate-500 mt-1" data-testid="send-loc-noresult">Koi customer nahi mila.</p>}
      {res.length > 0 && <div className="mt-1 rounded-xl border border-slate-100 bg-white divide-y divide-slate-100" data-testid="send-loc-results">
        {res.map((c, i) => <button key={c.ref} type="button" onClick={() => setPicked(c)} className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-sm flex items-center gap-2 min-w-0" data-testid={`send-loc-result-${i}`}>
          <UserRound className="w-4 h-4 text-emerald-700 flex-shrink-0" /><span className="min-w-0"><span className="block font-semibold truncate">{c.name}</span><span className="block text-xs text-slate-500 truncate">{c.hint}</span></span></button>)}
      </div>}
    </div>
  );
}

function SentList({ items, onStop }) {
  if (!items.length) return null;
  return (
    <div className="mt-3 divide-y divide-slate-100" data-testid="sent-locations">
      {items.map((s) => (
        <div key={s.id} className="py-2 flex flex-wrap items-center gap-2" data-testid={`sent-location-${s.id}`}>
          <div className="flex-1 min-w-0 text-sm"><div className="font-semibold text-slate-900 truncate">{s.customer_name}</div><div className="text-xs text-slate-600 truncate">{s.service_name}</div><div className="text-xs text-emerald-700">Aapki location bheji gayi · {fmt(s.expires_at)} tak</div></div>
          <button type="button" onClick={() => onStop(s)} className={danger} data-testid={`sent-location-stop-${s.id}`}><Trash2 className="w-3.5 h-3.5" /> Band karein</button>
        </div>
      ))}
    </div>
  );
}

// Provider shares own location with ANY chosen customer (optional, 24h, only that customer can open it).
export function SendLocationToCustomer() {
  const [services, setServices] = useState(null);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const [sid, setSid] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState([]);
  const loadSent = () => fetchSentLocationShares().then((l) => setSent(live(l))).catch(() => {});
  useEffect(() => {
    fetchLocationMyServices().then((l) => { setServices(l); setSid(l[0]?.id || ""); if (l.length) loadSent(); }).catch(() => setServices([]));
  }, []);
  if (!services?.length) return null;
  const send = async () => {
    if (!picked) return toast.info("Pehle customer chunein");
    if (!window.confirm(`Aapki current location sirf ${picked.name} ko 24 ghante ke liye dikhegi. Bhejein?`)) return;
    setBusy(true);
    try {
      const loc = await captureLocation();
      try { await sendLocationToCustomer({ customer_ref: picked.ref, service_id: sid, ...loc }); } catch (e) { throw new Error(apiFail(e, "Location bhejna")); }
      toast.success(`Location ${picked.name} ko bhej di gayi (24 ghante)`);
      setPicked(null); setOpen(false); loadSent();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const stop = async (s) => {
    try { await revokeLocationShare(s.id); toast.success("Location sharing band kar di"); } catch (e) { if (e?.response?.status !== 404) return toast.error(apiFail(e, "Sharing band")); toast.info(GONE); }
    setSent((l) => l.filter((x) => x.id !== s.id));
  };
  return (
    <div className="mt-3 pt-3 border-t border-slate-100" data-testid="send-location-to-customer">
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} className={`${btn} border border-emerald-200 text-emerald-800 hover:bg-emerald-50`} data-testid="send-loc-open"><Send className="w-3.5 h-3.5" /> Kisi customer ko apni location bhejein</button>
      ) : (
        <div className="space-y-2" data-testid="send-loc-panel">
          <div className="text-sm font-semibold text-slate-900">Customer ko apni location bhejein</div>
          <p className="text-xs text-slate-500">Optional hai. Location sirf chune gaye customer ko 24 ghante tak dikhegi, phir apne aap hat jaayegi.</p>
          <CustomerPicker picked={picked} setPicked={setPicked} />
          {services.length > 1 && (
            <select value={sid} onChange={(e) => setSid(e.target.value)} className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white" aria-label="Service" data-testid="send-loc-service">
              {services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          <div className="flex gap-2 flex-wrap">
            <button type="button" onClick={send} disabled={busy || !picked} className={primary} data-testid="send-loc-submit">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Location bhejein</button>
            <button type="button" onClick={() => { setOpen(false); setPicked(null); }} className={`${btn} border border-slate-200 text-slate-700`} data-testid="send-loc-cancel">Cancel</button>
          </div>
        </div>
      )}
      <SentList items={sent} onStop={stop} />
    </div>
  );
}
